import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Clock, Play, Pause, RotateCcw, ChevronRight, ClipboardList, BookOpen, ScrollText, UserCircle2, Stethoscope } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import Footer from '../components/Footer';
import { authFetch } from '../lib/authFetch';
import useAllowPrint from '../components/useAllowPrint';

const AXIOM_LOGO = 'https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png';

// Self-work items with codes
const SELF_WORK_ITEMS = [
  {
    id: 'sw-0001',
    code: 'SW-0001',
    title: 'The H.O.W. Commitment',
    description: 'Honesty, Open-Mindedness & Willingness: plus Substance Audit, Three-Fold Disease Impact & Motivation Review. Baseline for your Case Management Plan.',
    icon: 'commitment',
    route: '/self-work/task/sw-0001',
    estimatedTime: 45,
    week: -2 // 2 Week Window
  },
  {
    id: 'sw001',
    code: 'SW001',
    title: 'Stickman Assessment',
    description: 'Map your life areas (family, work, health, spirit) and identify how addiction has affected each part',
    icon: 'stickman',
    route: '/self-work/stickman',
    estimatedTime: 30,
    week: -2 // 2 Week Window
  }
];

// Assessment & Profile items — completed during the 2 Week Window.
// These are the "Assessment" side of the 2 Week Window Tasks (as surfaced
// from the Weekly Tasks dropdown on the dashboard). They deliberately live
// here so that clicking "2 Week Window Tasks" on the dashboard lands the
// user on a page that clearly shows what to complete for their admission
// profile.
const ASSESSMENT_ITEMS = [
  {
    id: 'personal-history',
    code: 'ASSESS-PH',
    title: 'Personal History',
    description: 'Your full admission assessment: background, health, substance use, family and readiness',
    icon: 'personal-history',
    route: '/personal-history',
    estimatedTime: 45,
    week: -2
  },
  {
    id: 'admission-assessment',
    code: 'ASSESS-ADM',
    title: 'Admission Assessment',
    description: 'Clinical admission questions: completed once during the 2 Week Window',
    icon: 'admission',
    route: '/admission-assessment',
    estimatedTime: 15,
    week: -2
  }
];

// Required Readings — shown under Readings section, not Self-Work
const REQUIRED_READINGS = [
  {
    id: 'sw002',
    code: 'WR-INTRO',
    title: 'Introduction to ARP',
    description: 'Understanding the programme, H.O.W. principles and the three-fold disease',
    icon: 'reading',
    route: '/introduction',
    estimatedTime: 90,
    week: -2
  }
];

// Weekly Reading items
const WEEKLY_READINGS = [
  {
    id: 'wr001',
    code: 'WR001',
    title: 'Understanding Alcoholism/Addiction',
    description: 'Physical, mental and emotional aspects of the disease',
    icon: 'reading',
    route: '/readings/wr001',
    estimatedTime: 90,
    week: 1
  }
];

function SelfWorkPage() {
  useAllowPrint();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  const [completedItems, setCompletedItems] = useState([]);
  const [progress, setProgress] = useState({});

  const isInTwoWeekWindow = user?.role === 'superadmin' ? false : (user?.current_week || -2) <= 0;

  useEffect(() => {
    fetchProgress();
  }, []);

  // Refetch when the tab regains focus / becomes visible so the assessment
  // cards turn green immediately when a user returns from /personal-history
  // or /admission-assessment.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchProgress();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
    };
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/self-work-progress`);
      if (response.ok) {
        const data = await response.json();
        setCompletedItems(data.completed || []);
        setProgress(data.progress || {});
      }
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  const isCompleted = (itemId) => completedItems.includes(itemId);
  const getProgress = (itemId) => progress[itemId] || 0;

  const SelfWorkCard = ({ item }) => {
    const completed = isCompleted(item.id);
    const itemProgress = getProgress(item.id);

    // Icon per item + subtle colour family so the sections read at a glance.
    const iconKey = item.icon || 'reading';
    const ICONS = {
      'personal-history': ClipboardList,
      'admission': Stethoscope,
      'reading': BookOpen,
      'commitment': ScrollText,
      'stickman': UserCircle2,
    };
    const FAMILY = {
      'personal-history': { bg: 'bg-amber-100', fg: 'text-amber-700', ring: 'border-amber-200 hover:border-amber-400', badge: 'bg-amber-100 text-amber-800' },
      'admission':        { bg: 'bg-amber-100', fg: 'text-amber-700', ring: 'border-amber-200 hover:border-amber-400', badge: 'bg-amber-100 text-amber-800' },
      'reading':          { bg: 'bg-sky-100',    fg: 'text-sky-700',   ring: 'border-sky-200 hover:border-sky-400',   badge: 'bg-sky-100 text-sky-800' },
      'commitment':       { bg: 'bg-purple-100', fg: 'text-purple-700',ring: 'border-purple-200 hover:border-purple-400', badge: 'bg-purple-100 text-purple-800' },
      'stickman':         { bg: 'bg-purple-100', fg: 'text-purple-700',ring: 'border-purple-200 hover:border-purple-400', badge: 'bg-purple-100 text-purple-800' },
    };
    const Icon = ICONS[iconKey] || BookOpen;
    const fam = FAMILY[iconKey] || FAMILY.reading;

    return (
      <button
        onClick={() => navigate(item.route)}
        className={`w-full text-left p-4 rounded-xl border-2 transition-all hover:shadow-lg ${
          completed
            ? 'bg-green-50 border-green-300'
            : `bg-white ${fam.ring}`
        }`}
        data-testid={`self-work-${item.id}`}
      >
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-xl ${completed ? 'bg-green-100' : fam.bg}`}>
            <Icon className={`w-6 h-6 ${completed ? 'text-green-600' : fam.fg}`} />
          </div>
          
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded ${
                completed ? 'bg-green-200 text-green-800' : fam.badge
              }`}>
                {item.code}
              </span>
              {completed && (
                <CheckCircle className="w-4 h-4 text-green-600" />
              )}
            </div>
            
            <h3 className={`font-bold ${completed ? 'text-green-800' : 'text-gray-800'}`}>
              {item.title}
            </h3>
            <p className="text-sm text-gray-600 mt-1">{item.description}</p>
            
            <div className="flex items-center gap-4 mt-3">
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {item.estimatedTime} min
              </span>
              
              {itemProgress > 0 && !completed && (
                <div className="flex-1 max-w-[100px]">
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-500 rounded-full"
                      style={{ width: `${itemProgress}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-gray-500">{itemProgress}%</span>
                </div>
              )}
            </div>
          </div>
          
          <ChevronRight className={`w-5 h-5 ${completed ? 'text-green-400' : 'text-gray-400'}`} />
        </div>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 pb-20">
      <PageHeader 
        title="Self-Work"
      />

      <main className="pt-20 px-4 pb-6">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Self-Work
          </h1>
          <p className="text-sm text-blue-100 mb-6">
            Complete these exercises to progress through the programme
          </p>

          {/* 2 Week Window Section */}
          {isInTwoWeekWindow && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-amber-400 text-amber-900 px-3 py-1 rounded-lg">
                  <span className="text-xs font-black uppercase tracking-wider">2 Week Window</span>
                </div>
              </div>

              {/* Ordered clinical intake flow:
                   1) Introduction: understand what the programme is
                   2) Assessments: capture history & clinical admission
                   3) Self-Work Tasks: H.O.W. commitment & stickman
              */}

              {/* 1) Required Readings */}
              {REQUIRED_READINGS.filter(item => item.week <= 0).length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-bold text-blue-200 uppercase tracking-wider mb-2">Start Here · Introduction</p>
                  <div className="space-y-3">
                    {REQUIRED_READINGS.filter(item => item.week <= 0).map(item => (
                      <SelfWorkCard key={item.id} item={item} />
                    ))}
                  </div>
                </div>
              )}

              {/* 2) Assessments: Personal History & Admission Assessment */}
              <div className="mb-4">
                <p className="text-xs font-bold text-blue-200 uppercase tracking-wider mb-2">Assessments</p>
                <div className="space-y-3">
                  {ASSESSMENT_ITEMS.map(item => (
                    <SelfWorkCard key={item.id} item={item} />
                  ))}
                </div>
              </div>

              {/* 3) Self-Work Tasks */}
              <div className="mt-4">
                <p className="text-xs font-bold text-blue-200 uppercase tracking-wider mb-2">Self-Work Tasks</p>
                <div className="space-y-3">
                  {SELF_WORK_ITEMS.filter(item => item.week <= 0).map(item => (
                    <SelfWorkCard key={item.id} item={item} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Explanation Section */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-6 border border-white/20">
            <h2 className="text-lg font-bold text-white mb-3">About Self-Work</h2>
            <div className="space-y-3 text-sm text-blue-100">
              <p>
                Self-work is not homework - it is the personal effort you invest in your Recovery. 
                Each exercise is designed to help you understand yourself better and build the 
                foundation for lasting sobriety.
              </p>
              <p>
                <strong className="text-white">SW (Self-Work)</strong> exercises require active participation 
                and reflection. Take your time with each one.
              </p>
              <p>
                <strong className="text-white">WR (Weekly Readings)</strong> are educational materials 
                that take approximately 90 minutes each. You can pause and resume at any time.
              </p>
            </div>
          </div>

          {/* Weekly Readings Preview */}
          {!isInTwoWeekWindow && (
            <div className="mb-8">
              <h2 className="text-lg font-bold text-white mb-4">Weekly Readings</h2>
              <div className="space-y-3">
                {WEEKLY_READINGS.filter(item => item.week === user?.current_week).map(item => (
                  <SelfWorkCard key={item.id} item={item} />
                ))}
              </div>
            </div>
          )}

          {/* All Items List (for reference) */}
          <div className="bg-white rounded-xl p-6">
            <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-4">
              All Programme Items
            </h2>
            <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-2">Assessments</p>
            <div className="space-y-2 text-sm mb-4">
              {ASSESSMENT_ITEMS.map(item => (
                <button
                  key={item.id}
                  onClick={() => navigate(item.route)}
                  className="flex items-center justify-between py-2 border-b border-gray-100 w-full text-left hover:bg-gray-50 rounded px-1"
                  data-testid={`all-assess-${item.id}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded">{item.code}</span>
                    <span className="text-gray-700">{item.title}</span>
                  </div>
                  {isCompleted(item.id) ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                </button>
              ))}
            </div>
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">Readings</p>
            <div className="space-y-2 text-sm mb-4">
              {[...REQUIRED_READINGS, ...WEEKLY_READINGS].map(item => (
                <button
                  key={item.id}
                  onClick={() => navigate(item.route)}
                  className="flex items-center justify-between py-2 border-b border-gray-100 w-full text-left hover:bg-gray-50 rounded px-1"
                  data-testid={`all-rd-${item.id}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">{item.code}</span>
                    <span className="text-gray-700">{item.title}</span>
                  </div>
                  {isCompleted(item.id) ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                </button>
              ))}
            </div>
            <p className="text-xs font-semibold text-purple-600 uppercase tracking-wider mb-2">Self-Work</p>
            <div className="space-y-2 text-sm">
              {SELF_WORK_ITEMS.map(item => (
                <button
                  key={item.id}
                  onClick={() => navigate(item.route)}
                  className="flex items-center justify-between py-2 border-b border-gray-100 w-full text-left hover:bg-gray-50 rounded px-1"
                  data-testid={`all-sw-${item.id}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded">{item.code}</span>
                    <span className="text-gray-700">{item.title}</span>
                  </div>
                  {isCompleted(item.id) ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default SelfWorkPage;
